import {
  Component,
  Input,
  OnInit
} from '@angular/core';
import { OnyxEvent } from '../../models/event';
import { Payment } from '../../models/payment';
import { ErrorHandlerService } from '../../../shared/utils/error-handler.service';
import { TranslateService } from '@ngx-translate/core';
import { PaymentStatusUtils } from 'app/event/utils/payment-status-utils';
import { EventStore } from 'app/event/service/event.store';
import { DropdownItem } from 'app/event/models/dropdown-item';
import { PaymentStore } from 'app/event/service/payment.store';
import { Observable } from 'rxjs/Observable';
import { AuthenticationStore } from 'app/permission/services/authentication.store';
import { UpperCasePipe } from '@angular/common';
import { PaymentType } from 'app/event/models/payment-type';
import { NonCommissionType } from 'app/event/models/non-commission-type';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { catchError, finalize, tap } from 'rxjs/operators';
import { PaymentStatus } from 'app/event/models/payment-status.enum';
import { PioneerValidators } from 'app/shared/utils/pioneer.validators';

@Component({
  selector: 'pioneer-payment-summary',
  templateUrl: './payment-summary.component.html'
})
export class PaymentSummaryComponent implements OnInit {

  @Input() latestPayment: Payment = null;
  @Input() prepayment: Payment = null;

  nonCommissionTypes: DropdownItem[];
  paidDirectTypes: DropdownItem[];

  isLastPaymentRequested: boolean;
  isLastPaymentRequestResentOrRequestRejected: boolean;

  paymentForm: FormGroup;

  isPropertyUser: boolean;

  onyxEvent: OnyxEvent;

  private latestFinalPayment: Payment = null;

  ui = {
    showRejectedMessage: false,
    strikeThroughPaymentLines: false,
    strikeThroughFinalPayment: false,
    strikeThroughPrepayment: false
  };

  constructor(
    private errorHandlerService: ErrorHandlerService,
    private translateService: TranslateService,
    private eventStore: EventStore,
    private paymentStore: PaymentStore,
    private authenticationStore: AuthenticationStore,
    private uppercasePipe: UpperCasePipe,
    private formBuilder: FormBuilder
  ) {}

  @Input()
  set event(event: OnyxEvent) {
    this.onyxEvent = event;
  }

  ngOnInit(): void {

    this.isPropertyUser = this.authenticationStore.isPropertyUser();

    if (!this.onyxEvent) {
      this.errorHandlerService.throw(
        'Input property onyxEvent is null in PaymentRecordComponent',
        this.translateService.instant('MESSAGES.ERROR.INPUT_PROPERTY_NULL', {
          property: 'onyxEvent',
          className: 'PaymentRecordComponent'
        })
      );
    }

    if (!this.latestPayment) {
      this.errorHandlerService.throw(
        'Input property latestPayment is null in PaymentRecordComponent',
        this.translateService.instant('MESSAGES.ERROR.INPUT_PROPERTY_NULL', {
          property: 'latestPayment',
          className: 'PaymentRecordComponent'
        })
      );
    }

    const isErrorFinalPayment = PaymentStatusUtils.isError(this.latestPayment.status);
    if (isErrorFinalPayment) {
      this.ui.showRejectedMessage = true;
      this.ui.strikeThroughPaymentLines = true;
      this.ui.strikeThroughFinalPayment = true;
    }

    if (this.prepayment) {
      if (PaymentStatusUtils.isError(this.prepayment.status)) {
        this.ui.showRejectedMessage = true;
        this.ui.strikeThroughPaymentLines = true;
        this.ui.strikeThroughPrepayment = true;
      }
    }

    this.addNonCommissionDueTypes();
    this.addPaidDirectTypes();

    this.paymentStore.isLastPaymentRequested.subscribe(isRequested => {
      this.isLastPaymentRequested = isRequested;
    });

    this.paymentStore.isLastPaymentRequestResentOrRequestRejected.subscribe(isReqResentOrRej => {
      this.isLastPaymentRequestResentOrRequestRejected = isReqResentOrRej;
    });

    this.paymentForm = this.formBuilder.group({
      'description': [ '', Validators.maxLength(255) ],
      'type': [ null, ],
      'commissionAmount': [ null, [ PioneerValidators.isValidAmount ]]
    });

    this.paymentStore.getLatestFinalPayment()
      .take(1)
      .subscribe((latestFinalPayment: Payment) => this.latestFinalPayment = latestFinalPayment);
  }

  toggleMasterBillPaidSetting(): void {
    const updatedModel = this.onyxEvent.merge({
      masterBillPaid: !this.onyxEvent.masterBillPaid,
    }) as OnyxEvent;

    this.eventStore.updateEventIndicators(updatedModel).subscribe();
  }

  showPaymentForm(): Observable<boolean> {
    return this.paymentStore.isLastPaymentRequestedOrRequestResentOrRequestRejected;
  }

  public isAuthorizedPayment() {
    return this.authenticationStore.hasPermission('AuthorizePayment') && this.isEventControlledByThisUserType();
  }

  getAmountPaidLabel(): string {
    const label = 'EVENT.COMMISSION.NON_COMM_AMOUNT_PAID';
    const params = {
      currency: this.uppercasePipe.transform(this.onyxEvent.currency)
    };
    return this.translateService.instant(label, params);
  }

  isPayDirect(): boolean {
    return this.nonCommType && this.nonCommType.key === PaymentType.PAID_DIRECT;
  }

  addNonCommissionDueTypes(): void {
    this.nonCommissionTypes = [
      new DropdownItem(PaymentType.PAID_DIRECT, NonCommissionType.PAID_DIRECT),
      new DropdownItem(PaymentType.EVENT_CANCELLED, NonCommissionType.EVENT_CANCELLED)
    ];
  }

  addPaidDirectTypes(): void {
    this.paidDirectTypes = [
      new DropdownItem(PaymentType.PAID_DIRECT, NonCommissionType.PAID_DIRECT)
    ];
  }

  get nonCommType(): DropdownItem {
    return this.getFormControlValue('type');
  }

  get nonCommAmountPaid(): number {
    return this.getFormControlValue('commissionAmount');
  }

  private getFormControlValue(name: string): any {
    return this.paymentForm ? this.paymentForm.get(name).value : null;
  }

  isFullAgencyNonCommSelected(): boolean {
    return this.authenticationStore.hasPermission('AgencyEventLevelIndicators') && this.isNonCommTypeSelected();
  }

  private isNonCommTypeSelected(): boolean {
    return this.nonCommType != null;
  }

  unSelectNonCommType(): void {
    this.paymentForm.reset({
      description: this.paymentForm.get('description').value,
      type: null,
      commissionAmount: null
    });
  }

  public isEventControlledByThisUserType() {
    return (this.authenticationStore.isAgencyUser() && this.onyxEvent.isAgencyControlled()) ||
        (this.isPropertyUser && this.onyxEvent.isPropertyControlled());
  }

  public saveNonCommPaymentType() {
    this.paymentForm.disable();

    const description = this.paymentForm.value.description;
    const paymentType = this.nonCommType.key;
    const commAmount = this.nonCommAmountPaid;

    const payment = new Payment({
      commissionAmount: commAmount,
      description: description,
      commissionCurrency: this.onyxEvent.currency,
      eventId: this.onyxEvent.eventId,
      type: paymentType
    });

    const obs$ = this.modifyPayment(payment).pipe(
      tap(() => {
        this.resetPaymentForm();
        const eventId = this.onyxEvent.eventId;

        this.paymentStore.loadPayments(eventId).subscribe();
        this.paymentStore.loadLatestFinalPayment(eventId).subscribe();
        this.paymentStore.loadFinalPaymentStatusList(eventId).subscribe();
        this.eventStore.getEvent(this.onyxEvent.eventId).subscribe();
      }),
      catchError(() => {
        return Observable.of(null);
      }),
      finalize(() => {
        this.paymentForm.enable();
      })
    );
    obs$.subscribe();
    return obs$;
  }

  private modifyPayment(payment: Payment): Observable<any> {
    if (this.latestFinalPayment !== null
      && (this.latestFinalPayment.status === PaymentStatus.REQUESTED)
          || (this.latestFinalPayment.status === PaymentStatus.REQUEST_REJECTED)
          || (this.latestFinalPayment.status === PaymentStatus.RESENT_REQUEST)) {
      return this.paymentStore.updateFinalPayment(payment).finally(() => {
      });
    } else {
      return this.paymentStore.addPayment(payment).finally(() => {
      });
    }
  }

  resetPaymentForm(): void {
    this.paymentForm.reset({
      description: '',
      type: null,
      commissionAmount: undefined
    });
  }

  disablePaidDirect(): boolean {
    let disableButton = false;
    if (this.isPayDirect() && (!this.nonCommAmountPaid || (this.nonCommAmountPaid && this.nonCommAmountPaid < 0))) {
      disableButton = true;
    }
    return disableButton;
  }

  onlyPaidDirect(): boolean {
    return this.isLastPaymentRequested || this.isLastPaymentRequestResentOrRequestRejected;
  }

}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/GroupPay/jobs/QA2/jobs/QA-Pioneer-ALL-2-POC/workspace/pioneer-web/src/app/event/payment/payment-summary/payment-summary.component.ts